import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    ArrowLeft, Edit2, User, Building, Mail, Phone, MapPin, Calendar,
    Package, Shield, Check, X, Hash, CreditCard, Upload, FileText,
    AlertCircle, Trash2, Download, Eye, Users, Award, TrendingUp,
    UserPlus, Crown, Medal, Star, Loader2, TrendingDown, ArrowUp
} from 'lucide-react';
import { clientService } from '@services/clientService';
import { assetService } from '@services/assetService';
import { kycService } from '@services/kycService';
import { useAuth } from '@context/AuthContext';
import DependantsList from './dependants/DependantsList';
import ClientAssetsList from '../assets/ClientAssetList.jsx';
import ClientKycDocuments from './ClientKycDocuments';
import ClientActivity from '../audit/ClientActivity';

const ClientDetailsView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { hasPermission } = useAuth();

    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [clientProducts, setClientProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'basic');
    const [deleteConfirmation, setDeleteConfirmation] = useState('');
    const [showDeleteWarning, setShowDeleteWarning] = useState(false);
    const [assetCount, setAssetCount] = useState(0);
    const [kycDocCount, setKycDocCount] = useState(0);

    const [dependants, setDependants] = useState([]);
    const [loadingDependants, setLoadingDependants] = useState(false);

    const [approvalLoading, setApprovalLoading] = useState(false);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState('');

    // Mock loyalty history - replace with actual data from API
    const [loyaltyHistory] = useState([
        {
            id: 1,
            tier: 'Bronze',
            productCount: 1,
            achievedDate: '2024-01-15',
            action: 'achieved'
        },
        {
            id: 2,
            tier: 'Silver',
            productCount: 2,
            achievedDate: '2024-04-20',
            action: 'upgraded'
        },
        {
            id: 3,
            tier: 'Gold',
            productCount: 3,
            achievedDate: '2024-08-10',
            action: 'upgraded'
        }
    ]);

    // Fetch client details
    useEffect(() => {
        const fetchClientDetails = async () => {
            try {
                setLoading(true);
                const response = await clientService.getById(id);
                console.log('Client details response:', response);

                // Handle the response structure
                const clientData = response.data || response;
                setClient(clientData);

                // Process products if they exist
                if (clientData.products && Array.isArray(clientData.products)) {
                    setClientProducts(clientData.products);
                } else if (clientData.clientProducts && Array.isArray(clientData.clientProducts)) {
                    setClientProducts(clientData.clientProducts);
                } else {
                    setClientProducts([]);
                }
                setLoadingProducts(false);
            } catch (err) {
                console.error('Error fetching client details:', err);
                setError(err.message || 'Failed to load client details');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchClientDetails();
        }
        const fetchAssetCount = async () => {
            try {
                const response = await assetService.clientAssets.getByClient(id);
                if (response.success && response.data) {
                    setAssetCount(response.data.length);
                }
            } catch (error) {
                console.error('Error fetching asset count:', error);
            }
        };

        if (id) {
            fetchAssetCount();
        }

        const fetchKycDocCount = async () => {
            try {
                const response = await kycService.clientKyc.getByClient(id);
                if (response.success && response.data) {
                    setKycDocCount(response.data.length);
                }
            } catch (error) {
                console.error('Error fetching KYC doc count:', error);
            }
        };

        if (id) {
            fetchKycDocCount();
        }
    }, [id]);

    useEffect(() => {
        const fetchDependants = async () => {
            if (!id) return;

            try {
                setLoadingDependants(true);
                const response = await clientService.dependants.getByClientId(id);

                console.log('Dependants response:', response);

                if (response.success && response.data) {
                    setDependants(response.data);
                } else if (Array.isArray(response)) {
                    // Fallback if response is not wrapped
                    setDependants(response);
                } else {
                    setDependants([]);
                }
            } catch (error) {
                console.error('Error fetching dependants:', error);
                setDependants([]);
            } finally {
                setLoadingDependants(false);
            }
        };

        if (id && client && client.clientType === 'RETAIL') {
            fetchDependants();
        }
    }, [id, client]);

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <Loader2 className="w-10 h-10 text-blue-600 animate-spin mx-auto mb-4" />
                    <p className="text-gray-600">Loading client details...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <AlertCircle className="w-10 h-10 text-red-600 mx-auto mb-4" />
                    <p className="text-gray-900 font-semibold mb-2">Error Loading Client</p>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={() => navigate('/clients')}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        Back to Clients
                    </button>
                </div>
            </div>
        );
    }

    // No client found
    if (!client) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <User className="w-10 h-10 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-900 font-semibold mb-2">Client Not Found</p>
                    <p className="text-gray-600 mb-4">The client you're looking for doesn't exist.</p>
                    <button
                        onClick={() => navigate('/clients')}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                        Back to Clients
                    </button>
                </div>
            </div>
        );
    }

    const isRetail = client.clientType === 'RETAIL';
    const isCorporate = client.clientType === 'CORPORATE';

    // Calculate loyalty tier based on product count
    const getLoyaltyTier = (productCount = clientProducts.length) => {
        if (productCount >= 5) return { tier: 'Platinum', color: 'purple', icon: Crown };
        if (productCount >= 3) return { tier: 'Gold', color: 'yellow', icon: Medal };
        if (productCount >= 2) return { tier: 'Silver', color: 'gray', icon: Star };
        if (productCount >= 1) return { tier: 'Bronze', color: 'orange', icon: Award };
        return null;
    };

    const loyaltyInfo = getLoyaltyTier();
    const isLoyaltySubscribed = client.loyaltySubscribed !== false && clientProducts.length > 0;

    // Get next tier information
    const getNextTier = () => {
        const currentCount = clientProducts.length;
        if (currentCount >= 5) return null; // Already at max tier
        if (currentCount >= 3) return { tier: 'Platinum', needed: 5 - currentCount };
        if (currentCount >= 2) return { tier: 'Gold', needed: 3 - currentCount };
        if (currentCount >= 1) return { tier: 'Silver', needed: 2 - currentCount };
        return { tier: 'Bronze', needed: 1 };
    };

    const nextTier = getNextTier();

    // Calculate profile completion
    const calculateCompletion = () => {
        const requiredFields = isRetail
            ? ['firstName', 'lastName', 'email', 'phoneNumber']
            : ['companyName', 'email', 'phoneNumber'];

        const optionalFields = isRetail
            ? ['middleName', 'nationalId', 'dateOfBirth', 'email2', 'phoneNumber2', 'address', 'city', 'country']
            : ['registrationNumber', 'taxIdNumber', 'industry', 'contactPersonName', 'email2', 'phoneNumber2', 'address', 'city', 'country'];

        const requiredFilled = requiredFields.filter(field => client[field] && client[field].toString().trim()).length;
        const optionalFilled = optionalFields.filter(field => client[field] && client[field].toString().trim()).length;

        const totalFields = requiredFields.length + optionalFields.length;
        const filledFields = requiredFilled + optionalFilled;

        return Math.round((filledFields / totalFields) * 100);
    };

    const completionPercentage = calculateCompletion();
    const isIncomplete = isRetail && (!client.nationalId || !client.dateOfBirth || !client.middleName);

    // Helper to render field with missing indicator
    const renderField = (value, label, defaultText = 'Not provided') => {
        const isMissing = !value || value === '';

        return (
            <div className={`${isMissing ? 'opacity-60' : ''}`}>
                <p className="text-xs text-gray-500">{label}</p>
                {isMissing ? (
                    <p className="text-sm text-orange-600 italic">{defaultText}</p>
                ) : (
                    <p className="text-sm text-gray-900">{value}</p>
                )}
            </div>
        );
    };

    const handleEditClient = () => {
        navigate(`/clients/${id}/edit`);
    };

    const handleApprove = async () => {
        try {
            setApprovalLoading(true);
            const response = await clientService.approve(id);
            if (response.success || response.data) {
                const updated = response.data || response;
                setClient(prev => ({ ...prev, ...updated }));
            }
        } catch (err) {
            console.error('Error approving client:', err);
            alert(err.message || 'Failed to approve client');
        } finally {
            setApprovalLoading(false);
        }
    };

    const handleReject = async () => {
        if (!rejectReason || rejectReason.trim().length < 10) {
            alert('Rejection reason must be at least 10 characters');
            return;
        }
        try {
            setApprovalLoading(true);
            const response = await clientService.reject(id, rejectReason.trim());
            if (response.success || response.data) {
                const updated = response.data || response;
                setClient(prev => ({ ...prev, ...updated }));
                setRejectModalOpen(false);
                setRejectReason('');
            }
        } catch (err) {
            console.error('Error rejecting client:', err);
            alert(err.message || 'Failed to reject client');
        } finally {
            setApprovalLoading(false);
        }
    };

    const handleViewProducts = () => {
        navigate(`/clients/${id}/products`);
    };

    const handleDeleteClient = async () => {
        if (deleteConfirmation.toLowerCase() === 'delete') {
            try {
                await clientService.delete(id);
                navigate('/clients');
            } catch (err) {
                console.error('Error deleting client:', err);
                alert('Failed to delete client');
            }
        }
    };

    return (
        <>
        <div className="min-h-screen bg-gray-50">
            <div className="p-6">
                <div className="space-y-6">
                    {/* Header with Profile Completion */}
                    <div className="bg-white p-4 rounded-lg shadow-sm">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/clients')}
                                className="p-2 hover:bg-gray-100 rounded-lg transition"
                                title="Back to clients list"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>

                            <div className="flex-1">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <h2 className="text-2xl font-semibold text-gray-900">Client Details</h2>

                                        {/* Profile Completion */}
                                        <div className="flex items-center gap-3">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm text-gray-500">Profile:</span>
                                                <div className="w-24 bg-gray-200 rounded-full h-2">
                                                    <div
                                                        className={`h-2 rounded-full transition-all ${
                                                            completionPercentage === 100 ? 'bg-green-600' :
                                                                completionPercentage >= 75 ? 'bg-blue-600' :
                                                                    completionPercentage >= 50 ? 'bg-yellow-600' :
                                                                        'bg-orange-600'
                                                        }`}
                                                        style={{ width: `${completionPercentage}%` }}
                                                    />
                                                </div>
                                                <span className="text-sm font-medium text-gray-700">{completionPercentage}%</span>
                                            </div>

                                            {isIncomplete && (
                                                <div className="flex items-center gap-2 text-orange-600">
                                                    <AlertCircle className="w-4 h-4" />
                                                    <span className="text-sm">Complete missing fields to finish profile</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        {/* Approval Status Badge */}
                                        {client.approvalStatus && (
                                            <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                                                client.approvalStatus === 'APPROVED' ? 'bg-green-100 text-green-800' :
                                                client.approvalStatus === 'REJECTED' ? 'bg-red-100 text-red-800' :
                                                'bg-yellow-100 text-yellow-800'
                                            }`}>
                                                {client.approvalStatus}
                                                {client.approvedBy && client.approvalStatus === 'APPROVED' && ` by ${client.approvedBy}`}
                                            </span>
                                        )}

                                        {/* Approve/Reject Buttons - visible to MANAGER+ */}
                                        {hasPermission('CLIENTS:APPROVE') && client.approvalStatus !== 'APPROVED' && (
                                            <button
                                                onClick={handleApprove}
                                                disabled={approvalLoading}
                                                className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-1 text-sm disabled:opacity-50"
                                            >
                                                <Check className="w-4 h-4" />
                                                Approve
                                            </button>
                                        )}
                                        {hasPermission('CLIENTS:APPROVE') && client.approvalStatus !== 'REJECTED' && (
                                            <button
                                                onClick={() => setRejectModalOpen(true)}
                                                disabled={approvalLoading}
                                                className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-1 text-sm disabled:opacity-50"
                                            >
                                                <X className="w-4 h-4" />
                                                Reject
                                            </button>
                                        )}

                                        {hasPermission('CLIENTS:UPDATE') && (
                                        <button
                                            onClick={handleEditClient}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                            Edit Client
                                        </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Client Header Info */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex items-center justify-between mb-6 pb-4 border-b">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                    {isCorporate ? (
                                        <Building className="w-6 h-6 text-blue-600" />
                                    ) : (
                                        <User className="w-6 h-6 text-blue-600" />
                                    )}
                                </div>
                                <div>
                                    <div className="flex items-center gap-3">
                                        <h3 className="text-xl font-bold text-gray-900">
                                            {isCorporate
                                                ? client.companyName || client.name
                                                : `${client.firstName || ''} ${client.middleName || ''} ${client.lastName || ''}`.trim() || client.name}
                                        </h3>
                                        {/* Loyalty Tier Badge */}
                                        {isLoyaltySubscribed && loyaltyInfo && (
                                            <div className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-gradient-to-r ${
                                                loyaltyInfo.tier === 'Platinum' ? 'from-purple-100 to-purple-50 border border-purple-200' :
                                                    loyaltyInfo.tier === 'Gold' ? 'from-yellow-100 to-yellow-50 border border-yellow-200' :
                                                        loyaltyInfo.tier === 'Silver' ? 'from-gray-100 to-gray-50 border border-gray-200' :
                                                            'from-orange-100 to-orange-50 border border-orange-200'
                                            }`}>
                                                <loyaltyInfo.icon className={`w-3.5 h-3.5 ${
                                                    loyaltyInfo.tier === 'Platinum' ? 'text-purple-600' :
                                                        loyaltyInfo.tier === 'Gold' ? 'text-yellow-600' :
                                                            loyaltyInfo.tier === 'Silver' ? 'text-gray-600' :
                                                                'text-orange-600'
                                                }`} />
                                                <span className={`text-xs font-medium ${
                                                    loyaltyInfo.tier === 'Platinum' ? 'text-purple-800' :
                                                        loyaltyInfo.tier === 'Gold' ? 'text-yellow-800' :
                                                            loyaltyInfo.tier === 'Silver' ? 'text-gray-800' :
                                                                'text-orange-800'
                                                }`}>
                                                    {loyaltyInfo.tier}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-500 mt-1">Code: {client.clientCode}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className={`px-3 py-1 text-sm rounded-full ${
                                    isCorporate
                                        ? 'bg-purple-100 text-purple-800'
                                        : 'bg-blue-100 text-blue-800'
                                }`}>
                                    {client.clientType}
                                </span>
                                <span className={`px-3 py-1 text-sm rounded-full ${
                                    client.active !== false
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-800'
                                }`}>
                                    {client.active !== false ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        </div>

                        {/* Tab Navigation */}
                        <div className="border-b mb-6">
                            <nav className="flex -mb-px">
                                <button
                                    onClick={() => setActiveTab('basic')}
                                    className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                                        activeTab === 'basic'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Basic Information
                                </button>
                                {hasPermission('KYC:READ') && (
                                <button
                                    onClick={() => setActiveTab('kyc')}
                                    className={`px-6 py-3 text-sm font-medium border-b-2 transition flex items-center gap-2 ${
                                        activeTab === 'kyc'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    KYC Documents
                                    {kycDocCount > 0 && (
                                        <span className={`inline-flex items-center justify-center px-2 py-1 text-xs font-bold rounded-full ${
                                            activeTab === 'kyc'
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-gray-200 text-gray-700'
                                        }`}>
                                            {kycDocCount}
                                        </span>
                                    )}
                                </button>
                                )}
                                <button
                                    onClick={() => setActiveTab('products')}
                                    className={`px-6 py-3 text-sm font-medium border-b-2 transition flex items-center gap-2 ${
                                        activeTab === 'products'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Products
                                    {clientProducts.length > 0 && (
                                        <span className={`inline-flex items-center justify-center px-2 py-1 text-xs font-bold rounded-full ${
                                            activeTab === 'products'
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-gray-200 text-gray-700'
                                        }`}>
                                            {clientProducts.length}
                                        </span>
                                    )}
                                </button>
                                <button
                                    onClick={() => setActiveTab('loyalty')}
                                    className={`px-6 py-3 text-sm font-medium border-b-2 transition flex items-center gap-2 ${
                                        activeTab === 'loyalty'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Loyalty Program
                                    {isLoyaltySubscribed && (
                                        <span className="inline-flex items-center justify-center w-2 h-2 bg-green-500 rounded-full"></span>
                                    )}
                                </button>
                                {hasPermission('DEPENDANTS:READ') && isRetail && (
                                <button
                                    onClick={() => setActiveTab('dependants')}
                                    className={`px-6 py-3 text-sm font-medium border-b-2 transition flex items-center gap-2 ${
                                        activeTab === 'dependants'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Dependants
                                    {dependants.length > 0 && (
                                        <span className={`inline-flex items-center justify-center px-2 py-1 text-xs font-bold rounded-full ${
                                            activeTab === 'dependants'
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-gray-200 text-gray-700'
                                        }`}>
                                            {dependants.length}
                                        </span>
                                    )}
                                </button>
                                )}
                                {hasPermission('ASSETS:READ') && (
                                <button
                                    onClick={() => setActiveTab('assets')}
                                    className={`px-6 py-3 text-sm font-medium border-b-2 transition flex items-center gap-2 ${
                                        activeTab === 'assets'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Assets
                                    {assetCount > 0 && (
                                        <span className={`inline-flex items-center justify-center px-2 py-1 text-xs font-bold rounded-full ${
                                            activeTab === 'assets'
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-gray-200 text-gray-700'
                                        }`}>
                                            {assetCount}
                                        </span>
                                    )}
                                </button>
                                )}
                                {hasPermission('AUDIT:READ') && (
                                <button
                                    onClick={() => setActiveTab('activity')}
                                    className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                                        activeTab === 'activity'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Activity
                                </button>
                                )}
                                <button
                                    onClick={() => setActiveTab('settings')}
                                    className={`px-6 py-3 text-sm font-medium border-b-2 transition ${
                                        activeTab === 'settings'
                                            ? 'border-blue-500 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    Settings
                                </button>
                            </nav>
                        </div>

                        {/* Tab Content */}
                        {activeTab === 'basic' && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {/* Column 1: Basic Information */}
                                <div className="space-y-4">
                                    <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Basic Information</h4>
                                    <div className="space-y-3">
                                        {isRetail ? (
                                            <>
                                                {renderField(
                                                    `${client.firstName || ''} ${client.middleName || ''} ${client.lastName || ''}`.trim(),
                                                    'Full Name'
                                                )}
                                                {renderField(client.nationalId, 'ID Number')}
                                                {renderField(client.kraPin, 'KRA PIN')}
                                                {renderField(
                                                    client.dateOfBirth ? new Date(client.dateOfBirth).toLocaleDateString() : null,
                                                    'Date of Birth'
                                                )}
                                                <div>
                                                    <p className="text-xs text-gray-500">Dependants</p>
                                                    <p className="text-sm text-gray-900">{dependants.length}</p>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                {renderField(client.companyName || client.name, 'Company Name')}
                                                {renderField(client.registrationNumber, 'Registration Number')}
                                                {renderField(client.taxIdNumber, 'Tax ID')}
                                                {renderField(client.industry, 'Industry')}
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Column 2: Contact Details */}
                                <div className="space-y-4">
                                    <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Contact Details</h4>
                                    <div className="space-y-3">
                                        {renderField(client.phoneNumber, 'Primary Phone')}
                                        {renderField(client.email, 'Primary Email')}
                                        {renderField(
                                            client.address ?
                                                `${client.address}${client.city ? ', ' + client.city : ''}${client.country ? ', ' + client.country : ''}`
                                                : null,
                                            'Address'
                                        )}
                                        {renderField(client.phoneNumber2, 'Secondary Phone')}
                                    </div>
                                </div>

                                {/* Column 3: Status & Metadata */}
                                <div className="space-y-4">
                                    <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Status & Metadata</h4>
                                    <div className="space-y-3">
                                        <div>
                                            <p className="text-xs text-gray-500">Total Products</p>
                                            <p className="text-sm text-gray-900 font-semibold">{clientProducts.length}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">Cross-sell Consent</p>
                                            <div className="flex items-center gap-1">
                                                {client.crossSellingConsent ? (
                                                    <>
                                                        <Check className="w-4 h-4 text-green-600" />
                                                        <span className="text-sm text-green-600">Given</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <X className="w-4 h-4 text-red-600" />
                                                        <span className="text-sm text-red-600">Not Given</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">Created</p>
                                            <p className="text-sm text-gray-900">
                                                {client.createdAt ? new Date(client.createdAt).toLocaleDateString() : 'N/A'}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-500">Last Updated</p>
                                            <p className="text-sm text-gray-900">
                                                {client.updatedAt ? new Date(client.updatedAt).toLocaleDateString() : 'N/A'}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'kyc' && (
                            <ClientKycDocuments clientId={client.id} client={client} />
                        )}

                        {activeTab === 'products' && (
                            <div className="space-y-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                        <Package className="w-5 h-5" />
                                        Product Details
                                    </h4>
                                    {clientProducts.length > 0 && (
                                        <button
                                            onClick={handleViewProducts}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
                                        >
                                            Manage Products
                                        </button>
                                    )}
                                </div>

                                {loadingProducts ? (
                                    <div className="p-8 text-center">
                                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                        <p className="mt-2 text-gray-500">Loading products...</p>
                                    </div>
                                ) : clientProducts.length > 0 ? (
                                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                                        <table className="w-full">
                                            <thead className="bg-gray-50 border-b">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Product Name
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Company
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Amount
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Purchase Date
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Status
                                                </th>
                                            </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200 bg-white">
                                            {clientProducts.map((product, index) => (
                                                <tr key={product.productId || index} className="hover:bg-gray-50">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {product.productName}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-900">
                                                            {product.companyName}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {product.amount ?
                                                                `KES ${Number(product.amount).toLocaleString()}` :
                                                                '-'}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-600">
                                                            {product.purchaseDate ?
                                                                new Date(product.purchaseDate).toLocaleDateString() :
                                                                'N/A'}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                                                product.status === 'ACTIVE'
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : product.status === 'SUSPENDED'
                                                                        ? 'bg-yellow-100 text-yellow-800'
                                                                        : 'bg-gray-100 text-gray-800'
                                                            }`}>
                                                                {product.status || 'UNKNOWN'}
                                                            </span>
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                                        <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                                        <p className="text-gray-500 mb-2">No products found for this client</p>
                                        <p className="text-sm text-gray-400">Add products to track client purchases</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'loyalty' && (
                            <div className="space-y-6">
                                {/* Current Tier Status */}
                                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-100">
                                    <div className="flex items-center justify-between mb-4">
                                        <h4 className="text-lg font-semibold text-gray-900">Current Loyalty Status</h4>
                                        {isLoyaltySubscribed ? (
                                            <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                                                Active Member
                                            </span>
                                        ) : (
                                            <span className="px-3 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded-full">
                                                Not Enrolled
                                            </span>
                                        )}
                                    </div>

                                    {isLoyaltySubscribed && loyaltyInfo ? (
                                        <div className="space-y-4">
                                            {/* Current Tier Card */}
                                            <div className={`bg-gradient-to-r p-6 rounded-lg text-white ${
                                                loyaltyInfo.tier === 'Platinum' ? 'from-purple-600 to-purple-400' :
                                                    loyaltyInfo.tier === 'Gold' ? 'from-yellow-600 to-yellow-400' :
                                                        loyaltyInfo.tier === 'Silver' ? 'from-gray-600 to-gray-400' :
                                                            'from-orange-600 to-orange-400'
                                            }`}>
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <loyaltyInfo.icon className="w-8 h-8" />
                                                            <span className="text-2xl font-bold">{loyaltyInfo.tier} Tier</span>
                                                        </div>
                                                        <p className="text-sm opacity-90">
                                                            {clientProducts.length} {clientProducts.length === 1 ? 'Product' : 'Products'}
                                                        </p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-sm opacity-90 mb-1">Member Since</p>
                                                        <p className="text-lg font-semibold">
                                                            {client.createdAt ? new Date(client.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'N/A'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Progress to Next Tier */}
                                            {nextTier && (
                                                <div className="bg-white rounded-lg p-4 border border-gray-200">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="text-sm font-medium text-gray-700">Progress to {nextTier.tier}</span>
                                                        <span className="text-sm text-gray-500">{nextTier.needed} more {nextTier.needed === 1 ? 'product' : 'products'} needed</span>
                                                    </div>
                                                    <div className="w-full bg-gray-200 rounded-full h-2">
                                                        <div
                                                            className="bg-blue-600 h-2 rounded-full transition-all"
                                                            style={{
                                                                width: `${(clientProducts.length / (clientProducts.length + nextTier.needed)) * 100}%`
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8">
                                            <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                            <p className="text-gray-600 mb-2">Not enrolled in loyalty program</p>
                                            <p className="text-sm text-gray-500">Purchase a product to join and start earning rewards</p>
                                        </div>
                                    )}
                                </div>

                                {/* Tier Benefits */}
                                <div className="bg-white rounded-lg border border-gray-200 p-6">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Loyalty Tier Benefits</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Bronze */}
                                        <div className="p-4 border-2 rounded-lg border-orange-200 bg-orange-50">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Award className="w-5 h-5 text-orange-600" />
                                                <span className="font-semibold text-orange-900">Bronze</span>
                                                <span className="text-xs text-orange-600">(1+ product)</span>
                                            </div>
                                            <ul className="text-sm text-gray-700 space-y-1">
                                                <li>• Basic member benefits</li>
                                                <li>• Email notifications</li>
                                            </ul>
                                        </div>

                                        {/* Silver */}
                                        <div className="p-4 border-2 rounded-lg border-gray-300 bg-gray-50">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Star className="w-5 h-5 text-gray-600" />
                                                <span className="font-semibold text-gray-900">Silver</span>
                                                <span className="text-xs text-gray-600">(2+ products)</span>
                                            </div>
                                            <ul className="text-sm text-gray-700 space-y-1">
                                                <li>• Priority support</li>
                                                <li>• 5% discount on new products</li>
                                            </ul>
                                        </div>

                                        {/* Gold */}
                                        <div className="p-4 border-2 rounded-lg border-yellow-300 bg-yellow-50">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Medal className="w-5 h-5 text-yellow-600" />
                                                <span className="font-semibold text-yellow-900">Gold</span>
                                                <span className="text-xs text-yellow-600">(3+ products)</span>
                                            </div>
                                            <ul className="text-sm text-gray-700 space-y-1">
                                                <li>• Dedicated account manager</li>
                                                <li>• 10% discount on new products</li>
                                                <li>• Early access to new offerings</li>
                                            </ul>
                                        </div>

                                        {/* Platinum */}
                                        <div className="p-4 border-2 rounded-lg border-purple-300 bg-purple-50">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Crown className="w-5 h-5 text-purple-600" />
                                                <span className="font-semibold text-purple-900">Platinum</span>
                                                <span className="text-xs text-purple-600">(5+ products)</span>
                                            </div>
                                            <ul className="text-sm text-gray-700 space-y-1">
                                                <li>• VIP support 24/7</li>
                                                <li>• 15% discount on all products</li>
                                                <li>• Exclusive events & offers</li>
                                                <li>• Complimentary annual review</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* Loyalty History */}
                                {isLoyaltySubscribed && loyaltyHistory.length > 0 && (
                                    <div className="bg-white rounded-lg border border-gray-200 p-6">
                                        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                            <TrendingUp className="w-5 h-5" />
                                            Loyalty History
                                        </h4>
                                        <div className="space-y-4">
                                            {loyaltyHistory.map((entry, index) => {
                                                const tierConfig = getLoyaltyTier(entry.productCount);
                                                const isLatest = index === loyaltyHistory.length - 1;

                                                return (
                                                    <div key={entry.id} className="flex items-start gap-4">
                                                        <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                                                            tierConfig.tier === 'Platinum' ? 'bg-purple-100' :
                                                                tierConfig.tier === 'Gold' ? 'bg-yellow-100' :
                                                                    tierConfig.tier === 'Silver' ? 'bg-gray-100' :
                                                                        'bg-orange-100'
                                                        }`}>
                                                            <tierConfig.icon className={`w-5 h-5 ${
                                                                tierConfig.tier === 'Platinum' ? 'text-purple-600' :
                                                                    tierConfig.tier === 'Gold' ? 'text-yellow-600' :
                                                                        tierConfig.tier === 'Silver' ? 'text-gray-600' :
                                                                            'text-orange-600'
                                                            }`} />
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-semibold text-gray-900">{entry.tier} Tier</span>
                                                                {entry.action === 'upgraded' && (
                                                                    <span className="flex items-center gap-1 text-green-600 text-sm">
                                                                        <ArrowUp className="w-4 h-4" />
                                                                        Upgraded
                                                                    </span>
                                                                )}
                                                                {isLatest && (
                                                                    <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                                                        Current
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-sm text-gray-600 mt-1">
                                                                {entry.productCount} {entry.productCount === 1 ? 'product' : 'products'} • {new Date(entry.achievedDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                                            </p>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'dependants' && (
                            <DependantsList
                                clientId={client.id}
                                isRetailClient={client.clientType === 'RETAIL'}
                            />
                        )}
                        {activeTab === 'assets' && (
                            <ClientAssetsList clientId={client.id} />
                        )}

                        {activeTab === 'activity' && hasPermission('AUDIT:READ') && (
                            <ClientActivity clientId={client.id} />
                        )}

                        {activeTab === 'settings' && (
                            <div className="space-y-6">
                                {/* Preferences */}
                                <div className="space-y-4">
                                    <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Preferences</h4>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="border rounded-lg p-4">
                                            <label className="flex items-start gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={client.crossSellingConsent}
                                                    disabled
                                                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded"
                                                />
                                                <div>
                                                    <span className="font-medium text-gray-900">Cross-selling Consent</span>
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        Client has {client.crossSellingConsent ? 'agreed' : 'not agreed'} to receive marketing communications
                                                    </p>
                                                </div>
                                            </label>
                                        </div>

                                        <div className="border rounded-lg p-4">
                                            <label className="flex items-start gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={client.active !== false}
                                                    disabled
                                                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded"
                                                />
                                                <div>
                                                    <span className="font-medium text-gray-900">Active Status</span>
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        This client is currently {client.active !== false ? 'active' : 'inactive'} in the system
                                                    </p>
                                                </div>
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* Danger Zone */}
                                {hasPermission('CLIENTS:DELETE') && (
                                <div className="border-t pt-6">
                                    <h4 className="text-sm font-semibold text-red-600 uppercase tracking-wider mb-4">Danger Zone</h4>

                                    <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                                        <div className="space-y-4">
                                            <div>
                                                <h5 className="font-medium text-gray-900 mb-2">Delete Client</h5>
                                                <p className="text-sm text-gray-600 mb-4">
                                                    Once you delete a client, there is no going back. Please be certain.
                                                </p>
                                            </div>

                                            {showDeleteWarning ? (
                                                <div className="space-y-3">
                                                    <p className="text-sm text-red-600 font-medium">
                                                        Type "delete" to confirm deletion:
                                                    </p>
                                                    <div className="flex gap-2">
                                                        <input
                                                            type="text"
                                                            value={deleteConfirmation}
                                                            onChange={(e) => setDeleteConfirmation(e.target.value)}
                                                            placeholder="Type delete to confirm"
                                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                                                        />
                                                        <button
                                                            onClick={handleDeleteClient}
                                                            disabled={deleteConfirmation.toLowerCase() !== 'delete'}
                                                            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition ${
                                                                deleteConfirmation.toLowerCase() === 'delete'
                                                                    ? 'bg-red-600 text-white hover:bg-red-700'
                                                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                            }`}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                            Confirm Delete
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setShowDeleteWarning(false);
                                                                setDeleteConfirmation('');
                                                            }}
                                                            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => setShowDeleteWarning(true)}
                                                    className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-100 flex items-center gap-2"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                    Delete Client
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>

            {/* Reject Modal */}
            {rejectModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Reject Client</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Please provide a reason for rejecting this client record.
                        </p>
                        <textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Rejection reason (minimum 10 characters)..."
                            className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                            rows={4}
                        />
                        <div className="flex justify-end gap-3 mt-4">
                            <button
                                onClick={() => { setRejectModalOpen(false); setRejectReason(''); }}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleReject}
                                disabled={approvalLoading || rejectReason.trim().length < 10}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                            >
                                {approvalLoading ? 'Rejecting...' : 'Reject Client'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ClientDetailsView;
